import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Cpu,
  LayoutDashboard,
  Loader2,
  Settings,
  User,
  Users,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { usePermissions, type Permissions } from "@/hooks/usePermissions";
import { globalSearch } from "@/api/search";
import { getStatusColor, scoreColor } from "@/lib/constants";
import { getEffectivePipelineStatuses } from "@/lib/projectConfig";
import type {
  SearchCompanyResult,
  SearchContactResult,
  SearchResponse,
} from "@/api/types";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PageEntry {
  label: string;
  path: string;
  icon: typeof LayoutDashboard;
  visible: (p: Permissions) => boolean;
}

const PAGES: PageEntry[] = [
  {
    label: "Dashboard",
    path: "dashboard",
    icon: LayoutDashboard,
    visible: (p) => p.canViewDashboard,
  },
  {
    label: "Companies",
    path: "companies",
    icon: Building2,
    visible: () => true,
  },
  {
    label: "Contacts",
    path: "contacts",
    icon: Users,
    visible: (p) => p.canViewContacts,
  },
  {
    label: "Engine Control",
    path: "engine",
    icon: Cpu,
    visible: (p) => p.canViewEngine,
  },
  {
    label: "Settings",
    path: "settings",
    icon: Settings,
    visible: (p) => p.canViewSettings,
  },
];

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { project } = useCurrentProject();
  const perms = usePermissions();
  const currentSlug = project?.slug ?? "";

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);

  const [results, setResults] = useState<SearchResponse>({
    companies: [],
    contacts: [],
  });
  // The query `results` answer: rows stay on screen while a newer query is
  // on its way, but only an answer to the query typed can be opened.
  const [answered, setAnswered] = useState("");
  const [loading, setLoading] = useState(false);

  // Reset query when the palette closes so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults({ companies: [], contacts: [] });
      setAnswered("");
      setLoading(false);
    }
  }, [open]);

  // Hit /search when the debounced query meets the backend's 2-char minimum.
  useEffect(() => {
    if (!open) return;
    const trimmed = debouncedQuery.trim();
    if (trimmed.length < 2) {
      setResults({ companies: [], contacts: [] });
      setAnswered(trimmed);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    globalSearch(trimmed, 5)
      .then((res) => {
        if (cancelled) return;
        setResults(res);
        setAnswered(trimmed);
      })
      .catch(() => {
        if (cancelled) return;
        setResults({ companies: [], contacts: [] });
        setAnswered(trimmed);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, debouncedQuery]);

  const trimmed = query.trim();
  const visiblePages = useMemo(() => {
    return PAGES.filter((p) => p.visible(perms)).filter(
      (p) =>
        trimmed === "" ||
        p.label.toLowerCase().includes(trimmed.toLowerCase()),
    );
  }, [perms, trimmed]);

  function pickCompany(c: SearchCompanyResult) {
    onOpenChange(false);
    navigate(`/projects/${c.project_slug}/companies/${c.id}`);
  }

  function pickContact(c: SearchContactResult) {
    onOpenChange(false);
    // Land on the contacts page and let it auto-open the sheet via the
    // `?contact=ID` query param.
    navigate(`/projects/${c.project_slug}/contacts?contact=${c.id}`);
  }

  function pickPage(path: string) {
    onOpenChange(false);
    if (currentSlug) {
      navigate(`/projects/${currentSlug}/${path}`);
    }
  }

  const showLoading = loading && trimmed.length >= 2;
  const hasApiResults =
    results.companies.length > 0 || results.contacts.length > 0;
  const stale = answered !== trimmed;

  // The server matched and ranked the rows, by a director's name or a phone
  // number too: cmdk's own filter would hide those, their label lacks the
  // words typed, so it is off (pages are filtered above). The selection is
  // ours as well: cmdk keeps a row selected after the server's answer
  // replaced it, and Enter then opened nothing. The first row that can be
  // opened is selected whenever the selected one cannot.
  const openableRows = [
    ...(stale ? [] : results.companies.map(companyRow)),
    ...(stale ? [] : results.contacts.map(contactRow)),
    ...(currentSlug ? visiblePages.map(pageRow) : []),
  ];
  const [selected, setSelected] = useState("");
  const selectedOpenable = openableRows.includes(selected);
  const firstOpenable = openableRows[0] ?? "";
  useEffect(() => {
    if (!selectedOpenable) setSelected(firstOpenable);
  }, [selectedOpenable, firstOpenable]);

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      commandProps={{
        shouldFilter: false,
        value: selected,
        onValueChange: setSelected,
      }}
    >
      <CommandInput
        placeholder="Search companies, contacts or a phone number…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[400px]">
        {showLoading && (
          <div className="flex items-center gap-2 px-3 py-3 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Searching…
          </div>
        )}

        {!showLoading && !hasApiResults && visiblePages.length === 0 && (
          <CommandEmpty>No results found.</CommandEmpty>
        )}

        {results.companies.length > 0 && (
          <CommandGroup heading="Companies">
            {results.companies.map((c) => (
              <CommandItem
                key={`company-${c.project_slug}-${c.id}`}
                value={companyRow(c)}
                onSelect={() => pickCompany(c)}
                disabled={stale}
                className={cn("gap-3", stale && "opacity-50")}
              >
                <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-slate-900">
                      {c.name}
                    </span>
                    {c.domain && (
                      <span className="truncate text-[12px] text-slate-400">
                        {c.domain}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400">
                    <StatusPill
                      status={c.pipeline_status}
                      statuses={getEffectivePipelineStatuses(project)}
                    />
                    <span>{c.project_name}</span>
                  </div>
                </div>
                {perms.canViewScore && c.score != null && (
                  <span
                    className={cn(
                      "shrink-0 text-sm font-medium tabular-nums",
                      scoreColor(c.score),
                    )}
                  >
                    {c.score}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.companies.length > 0 && results.contacts.length > 0 && (
          <CommandSeparator />
        )}

        {results.contacts.length > 0 && (
          <CommandGroup heading="Contacts">
            {results.contacts.map((c) => (
              <CommandItem
                key={`contact-${c.project_slug}-${c.id}`}
                value={contactRow(c)}
                onSelect={() => pickContact(c)}
                disabled={stale}
                className={cn("gap-3", stale && "opacity-50")}
              >
                <User className="h-4 w-4 shrink-0 text-slate-400" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900">
                    {c.full_name}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[12px] text-slate-400">
                    {c.email && <span className="truncate">{c.email}</span>}
                    {c.email && c.company_name && (
                      <span className="text-slate-300">·</span>
                    )}
                    {c.company_name && (
                      <span className="truncate">{c.company_name}</span>
                    )}
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {hasApiResults && visiblePages.length > 0 && <CommandSeparator />}

        {visiblePages.length > 0 && (
          <CommandGroup heading="Pages">
            {visiblePages.map((p) => {
              const Icon = p.icon;
              return (
                <CommandItem
                  key={p.path}
                  value={pageRow(p)}
                  onSelect={() => pickPage(p.path)}
                  className="gap-3"
                  disabled={!currentSlug}
                >
                  <Icon className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="text-sm text-slate-800">{p.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}

// A row's value, as cmdk keeps it (trimmed): the selection is compared to it.
function companyRow(c: SearchCompanyResult): string {
  return `company ${c.project_slug} ${c.id} ${c.name}`.trim();
}

function contactRow(c: SearchContactResult): string {
  return `contact ${c.project_slug} ${c.id} ${c.full_name}`.trim();
}

function pageRow(p: PageEntry): string {
  return `page ${p.path}`;
}

function StatusPill({
  status,
  statuses,
}: {
  status: string;
  statuses: { key: string; label: string; color?: string }[];
}) {
  const colors = getStatusColor(status, statuses);
  const label =
    statuses.find((s) => s.key === status)?.label ??
    status.replace(/_/g, " ");
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-1.5 py-0 text-[10px] font-medium",
        colors.bg,
        colors.text,
        colors.border,
      )}
    >
      <span className={cn("h-1 w-1 rounded-full", colors.dot)} />
      <span className="capitalize">{label}</span>
    </span>
  );
}
